// Client-side stall guard for the KA Intelligence stream.
//
// A visitor must never be left indefinitely on "Working…". If the answer
// stream produces no first chunk, or goes silent mid-answer, the guard ends
// the stream with a safe, public message so the UI shows a real failure and
// the composer becomes usable again.
//
// This guards only the browser→site connection. It never aborts the upstream
// model request on a timer at the server boundary, and it carries no
// reasoning, provenance or memory semantics.

export const STALL_FIRST_CHUNK_MS = 90_000;
export const STALL_IDLE_MS = 60_000;

export const STALL_MESSAGE =
  "The answer stopped arriving. Please try again.";

export interface StallGuardOptions {
  firstChunkMs?: number;
  idleMs?: number;
  /** Injectable clock hooks for tests. */
  setTimer?: typeof setTimeout;
  clearTimer?: typeof clearTimeout;
}

/**
 * Wrap a streaming Response so that a stalled body fails visibly.
 * Non-streaming responses (no body) pass through untouched.
 */
export function withStallGuard(response: Response, options: StallGuardOptions = {}): Response {
  if (!response.body) return response;

  const firstChunkMs = options.firstChunkMs ?? STALL_FIRST_CHUNK_MS;
  const idleMs = options.idleMs ?? STALL_IDLE_MS;
  const setTimer = options.setTimer ?? setTimeout;
  const clearTimer = options.clearTimer ?? clearTimeout;

  const reader = response.body.getReader();
  let timer: ReturnType<typeof setTimeout> | undefined;
  let done = false;

  const body = new ReadableStream<Uint8Array>({
    start(controller) {
      let sawChunk = false;

      const stop = () => {
        if (timer !== undefined) clearTimer(timer);
        timer = undefined;
      };

      const arm = (ms: number) => {
        stop();
        timer = setTimer(() => {
          if (done) return;
          done = true;
          void reader.cancel(STALL_MESSAGE).catch(() => {});
          controller.error(new Error(STALL_MESSAGE));
        }, ms);
      };

      const pump = async () => {
        try {
          while (true) {
            const chunk = await reader.read();
            if (done) return;
            if (chunk.done) {
              done = true;
              stop();
              controller.close();
              return;
            }
            sawChunk = true;
            controller.enqueue(chunk.value);
            arm(idleMs);
          }
        } catch (error) {
          if (done) return;
          done = true;
          stop();
          controller.error(error);
        }
      };

      arm(sawChunk ? idleMs : firstChunkMs);
      void pump();
    },
    cancel(reason?: unknown) {
      done = true;
      if (timer !== undefined) clearTimer(timer);
      return reader.cancel(reason);
    },
  });

  return new Response(body, {
    status: response.status,
    statusText: response.statusText,
    headers: response.headers,
  });
}
